import { formatLabValue } from './lab-results'
import {
  getMedicalDocumentAnalysisState,
  getMedicalDocumentClinicalDate,
  getMedicalDocumentDoctor,
  getMedicalDocumentDisplayTitle,
  getMedicalDocumentTimelineSummary,
  inferMedicalDocumentSpecialty
} from './medical-document-presentation'
import type {
  HealthEntry,
  LabResult,
  MedicalDocument,
  MedicationHistory
} from '@/types/health'

type MedicationHistoryWithMedication = MedicationHistory & {
  medication?: {
    name?: string | null
  } | null
}

export type UnifiedMedicalTimelineKind = 'document' | 'biology' | 'journal' | 'treatment'
export type UnifiedMedicalTimelineSeverity = 'high' | 'moderate' | 'normal'

export interface UnifiedMedicalTimelineItem {
  id: string
  kind: UnifiedMedicalTimelineKind
  date: string
  title: string
  subtitle: string | null
  detail: string
  severity: UnifiedMedicalTimelineSeverity
  specialtyLabel: string | null
  previewDocumentId: string | null
}

function normalizeText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function uniqueItems(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])]
}

function compareDateDesc(left: string, right: string): number {
  return Date.parse(right) - Date.parse(left)
}

function isFiniteDate(value: string | null | undefined): value is string {
  return Boolean(value && Number.isFinite(Date.parse(value)))
}

function getComparableLabValue(result: LabResult): number | null {
  const value = result.normalized_value ?? result.result_value
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function getComparableLabUnit(result: LabResult): string | null {
  return result.normalized_unit || result.unit_group_label || result.unit || null
}

function getSeverityRank(severity: UnifiedMedicalTimelineSeverity): number {
  if (severity === 'high') return 0
  if (severity === 'moderate') return 1
  return 2
}

function buildDocumentItems(documents: MedicalDocument[]): UnifiedMedicalTimelineItem[] {
  return documents
    .filter((document) => isFiniteDate(getMedicalDocumentClinicalDate(document).rawDate))
    .map((document) => {
      const clinicalDate = getMedicalDocumentClinicalDate(document)
      const analysisState = getMedicalDocumentAnalysisState(document)
      const extractedData = document.extracted_data as Record<string, unknown> | null
      const abnormalAnalysisCount =
        typeof extractedData?.abnormal_analysis_count === 'number'
          ? extractedData.abnormal_analysis_count
          : 0
      const severity: UnifiedMedicalTimelineSeverity =
        document.is_urgent || abnormalAnalysisCount >= 2 || analysisState.reviewRequired
          ? 'high'
          : abnormalAnalysisCount > 0 || analysisState.confidenceLabel === 'medium'
            ? 'moderate'
            : 'normal'

      return {
        id: `document-${document.id}`,
        kind: 'document',
        date: clinicalDate.rawDate,
        title: getMedicalDocumentDisplayTitle(document),
        subtitle: uniqueItems([
          inferMedicalDocumentSpecialty(document),
          getMedicalDocumentDoctor(document),
          document.medical_facility
        ]).join(' - '),
        detail: getMedicalDocumentTimelineSummary(document),
        severity,
        specialtyLabel: inferMedicalDocumentSpecialty(document),
        previewDocumentId: document.id
      } satisfies UnifiedMedicalTimelineItem
    })
}

function buildBiologyFallbackItems(
  labResults: LabResult[],
  documentsById: Map<string, MedicalDocument>
): UnifiedMedicalTimelineItem[] {
  const groups = new Map<string, LabResult[]>()

  labResults.forEach((result) => {
    if (!isFiniteDate(result.test_date)) {
      return
    }

    if (result.document_id && documentsById.has(result.document_id)) {
      return
    }

    const key = result.test_date
    const bucket = groups.get(key) || []
    bucket.push(result)
    groups.set(key, bucket)
  })

  return [...groups.entries()].map(([date, results]) => {
    const abnormalCount = results.filter((result) => result.is_abnormal).length
    const keyTests = uniqueItems(results.map((result) => result.test_name)).slice(0, 3)

    return {
      id: `biology-${date}`,
      kind: 'biology',
      date,
      title: abnormalCount > 0 ? 'Biologie a surveiller' : 'Biologie extraite',
      subtitle: uniqueItems([
        results[0]?.laboratory_name || null,
        results[0]?.doctor_name || null
      ]).join(' - '),
      detail:
        abnormalCount > 0
          ? `${abnormalCount} valeur${abnormalCount > 1 ? 's' : ''} a surveiller${keyTests.length > 0 ? `: ${keyTests.join(', ')}` : ''}`
          : `${results.length} mesure${results.length > 1 ? 's' : ''}${keyTests.length > 0 ? `: ${keyTests.join(', ')}` : ''}`,
      severity: abnormalCount > 1 ? 'high' : abnormalCount > 0 ? 'moderate' : 'normal',
      specialtyLabel: 'Biologie',
      previewDocumentId: null
    } satisfies UnifiedMedicalTimelineItem
  })
}

function buildJournalItems(healthEntries: HealthEntry[]): UnifiedMedicalTimelineItem[] {
  return healthEntries
    .filter((entry) => {
      if (!isFiniteDate(entry.entry_date)) {
        return false
      }

      return Boolean(
        entry.summary ||
        entry.urgent_alert ||
        entry.bleeding_present ||
        (entry.pain_level || 0) >= 6 ||
        (entry.energy_level || 10) <= 4 ||
        (entry.symptoms || []).length > 0
      )
    })
    .map((entry) => {
      const symptomSummary = uniqueItems(entry.symptoms || []).slice(0, 3).join(', ')
      const detail = entry.summary || symptomSummary || 'Point clinique saisi dans le journal.'
      const subtitle = uniqueItems([
        typeof entry.pain_level === 'number' ? `Douleur ${entry.pain_level}/10` : null,
        typeof entry.energy_level === 'number' ? `Energie ${entry.energy_level}/10` : null,
        entry.bleeding_present ? 'Saignement note' : null
      ]).join(' - ')

      return {
        id: `journal-${entry.id}`,
        kind: 'journal',
        date: entry.entry_date,
        title: 'Journal quotidien',
        subtitle: subtitle || null,
        detail,
        severity:
          entry.urgent_alert || entry.bleeding_present
            ? 'high'
            : (entry.pain_level || 0) >= 6 || (entry.energy_level || 10) <= 4
              ? 'moderate'
              : 'normal',
        specialtyLabel: 'Suivi quotidien',
        previewDocumentId: null
      } satisfies UnifiedMedicalTimelineItem
    })
}

function describeTreatmentAction(action: MedicationHistory['action']): string {
  switch (action) {
    case 'started':
      return 'Debut traitement'
    case 'paused':
      return 'Pause traitement'
    case 'resumed':
      return 'Reprise traitement'
    case 'stopped':
      return 'Arret traitement'
    case 'completed':
      return 'Fin traitement'
    default:
      return 'Modification traitement'
  }
}

function buildTreatmentItems(history: MedicationHistory[]): UnifiedMedicalTimelineItem[] {
  return (history as MedicationHistoryWithMedication[])
    .filter((entry) => isFiniteDate(entry.change_date))
    .filter((entry) =>
      ['started', 'modified', 'paused', 'resumed', 'stopped', 'completed'].includes(entry.action)
    )
    .map((entry) => {
      const medicationName = entry.medication?.name?.trim() || 'Traitement'
      const dosageSummary = uniqueItems([entry.new_dosage, entry.previous_dosage]).join(' -> ')

      return {
        id: `treatment-${entry.id}`,
        kind: 'treatment',
        date: entry.change_date,
        title: describeTreatmentAction(entry.action),
        subtitle: medicationName,
        detail: uniqueItems([
          entry.reason,
          dosageSummary || null,
          entry.notes
        ]).join(' - ') || 'Modification de traitement enregistree.',
        severity:
          entry.action === 'stopped' || entry.action === 'paused'
            ? 'high'
            : entry.action === 'modified' || entry.action === 'resumed'
              ? 'moderate'
              : 'normal',
        specialtyLabel: 'Traitement',
        previewDocumentId: null
      } satisfies UnifiedMedicalTimelineItem
    })
}

export function buildUnifiedMedicalTimeline(input: {
  documents: MedicalDocument[]
  labResults: LabResult[]
  healthEntries: HealthEntry[]
  medicationHistory: MedicationHistory[]
  limit?: number
}): UnifiedMedicalTimelineItem[] {
  const documentsById = new Map(input.documents.map((document) => [document.id, document]))

  return [
    ...buildDocumentItems(input.documents),
    ...buildBiologyFallbackItems(input.labResults, documentsById),
    ...buildJournalItems(input.healthEntries),
    ...buildTreatmentItems(input.medicationHistory)
  ]
    .filter((item) => isFiniteDate(item.date))
    .sort((left, right) => {
      const dateDiff = compareDateDesc(left.date, right.date)
      if (dateDiff !== 0) {
        return dateDiff
      }

      const severityDiff = getSeverityRank(left.severity) - getSeverityRank(right.severity)
      if (severityDiff !== 0) {
        return severityDiff
      }

      return left.title.localeCompare(right.title, 'fr')
    })
    .slice(0, input.limit || 24)
}

export function buildTimelineLabHighlight(result: LabResult): string {
  return [
    result.test_name,
    formatLabValue(
      getComparableLabValue(result),
      getComparableLabUnit(result),
      result.result_text
    )
  ]
    .filter(Boolean)
    .join(' ')
}

export function detectTimelineLabFocus(result: LabResult): string | null {
  const normalizedName = normalizeText(result.test_name)

  if (/crp|calprotectine|albumine|digest|leucocytes|plaquettes/.test(normalizedName)) {
    return 'Biologie'
  }

  if (/ferritine|hemoglobine|hematocrite|vgm|tcmh|ccmh|transferrine/.test(normalizedName)) {
    return 'Hematologie'
  }

  return null
}
